// src/components/support/forms/TakeBreak.js
import React, { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { useRequestProcessor } from "../../../requestProcessor";

import SpinnerLoading from "../../common/SpinnerLoading";
import { handleTakeABreak } from "../../../queryfunctions/queryfunctions";
import { useAtom } from "jotai";
import { childList } from "../../../atomstore/store";
import FootprintDatePicker from "../../common/FootprintDatePicker";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../../common/CustomSelect";

const getMaxAllowedToDateFromStart = (start) => {
    if (!start) return null;
    const startDate = moment(start);
    if (!startDate.isValid()) return null;
    if (startDate.date() === 1) return startDate.clone().endOf("month");
    return startDate.clone().add(29, "days");
};

const TakeBreak = () => {
    const navigate = useNavigate();
    const { useMutationWrapper } = useRequestProcessor();
    const [children] = useAtom(childList)
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [reason, setReason] = useState("");
    const [error, setError] = useState("");
    const firstNonPausedIndex = children.findIndex(c => c?.pause_state !== 1);
    const [selectedChild, setSelectedChild] = useState(firstNonPausedIndex >= 0 ? firstNonPausedIndex : 0)
    const isRestrictionActive = moment().isSameOrAfter("2026-04-01", "day");

    const takeABreak = useMutationWrapper(
        ["takeABreak"],
        () => {
            console.log("[Take a break] API Request:", { fromDate, toDate, reason });
            return handleTakeABreak(fromDate, toDate, reason, children[selectedChild]);
        },
        {
            onSuccess: (data) => {
                console.log("[Take a break] API Success:", data);
            },
        }
    );

    console.log("Selected Child", children[selectedChild])
    const validate = () => {
        if (!fromDate) {
            setError("Please select From Date");
            return false;
        } else if (!toDate) {
            setError("Please select To Date");
            return false;
        } else if (isRestrictionActive && fromDate && toDate) {
            const maxAllowed = getMaxAllowedToDateFromStart(fromDate);
            if (maxAllowed && moment(toDate).isAfter(maxAllowed, "day")) {
                setError(`Break end date cannot be after ${maxAllowed.format("MMM DD, YYYY")}`);
                return false;
            }
        } else if (!reason.trim()) {
            setError("Please enter a reason");
            return false;
        }
        setError("");
        return true;
    };

    const submitRequest = () => {
        if (validate()) {
            console.log("[Take a break] Submitting form:", { fromDate, toDate, reason });
            takeABreak.mutate();
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {takeABreak.isLoading && <SpinnerLoading />}

            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Take a Break</h2>
            </div>

            <div className="p-4 space-y-4">
                {children.filter(c => c?.pause_state !== 1).length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Child
                        </label>

                        <CustomSelect
                            label="Select Child"
                            value={selectedChild}
                            onChange={(val) => setSelectedChild(Number(val))}
                            options={children
                                .map((child, idx) => ({ value: idx, label: `${child?.name} ${child?.last_name}` }))
                                .filter(opt => children[opt.value]?.pause_state !== 1)
                            }
                        />
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        From Date
                    </label>
                    {/* <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500"
                    /> */}
                    <FootprintDatePicker
                        onChange={(date) => {
                            setFromDate(moment(date));
                            setError("");
                        }}
                        minimumDate={moment().add(1, "day").startOf("day")}
                        inputProps={{
                            placeholder: "Select Date",
                        }}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        To Date
                    </label>
                    <FootprintDatePicker
                        onChange={(date) => {
                            setToDate(moment(date));
                            setError("");
                        }}
                        minimumDate={fromDate || moment()}
                        maximumDate={
                            isRestrictionActive && fromDate
                                ? getMaxAllowedToDateFromStart(fromDate)
                                : null
                        }
                        placeholder="Select Date"
                        disabled={!fromDate}
                        inputProps={{
                            disabled: !fromDate,
                            placeholder: "Select Date",
                        }}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reason
                    </label>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Please share the reason for the break..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 h-32 outline-none focus:border-transparent"
                    />
                </div>

                {error && <p className="text-red-500 text-sm">{error}</p>}

                <button
                    onClick={submitRequest}
                    disabled={takeABreak.isLoading}
                    className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold hover:bg-cyan-600 transition disabled:opacity-60"
                >
                    Submit Request
                </button>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-blue-800 mb-2">
                        Terms &amp; Conditions
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-blue-800">
                        <li>30-day pause: 50% fee adjustment for that month</li>
                        <li>&lt;30 days: Treated as Leaves — no discount</li>
                        <li>60-day gap required between consecutive full-month pauses</li>
                        <li>Apply pause at least 1 day in advance</li>
                        <li>
                            Corporate Admission: Pause policy varies by contract — Check your
                            specific corporate agreement for details.
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default TakeBreak;
